<template>
  <div class="lpr-wrap" v-if="pairs.length">
    <div class="toolbar">
      <label>
        Pair:
        <select v-model.number="pairIdx">
          <option v-for="(p,i) in pairs" :key="i" :value="i">
						{{ pairLabel(p) }}
					</option>
        </select>
      </label>
			<p>e-corr: {{ this.eCorr }}</p>
    </div>

    <div class="svg-scroller">
      <svg ref="svgEl" :width="width" :height="height">
        <g :transform="`translate(${margin.left},${margin.top})`">
          <g v-for="gx in xTicks" :key="'gx-'+gx">
            <line class="grid" :x1="x(gx)" :x2="x(gx)" y1="0" :y2="innerH"/>
            <text class="axis-label" :x="x(gx)" :y="innerH+14" text-anchor="middle">{{ tickFmtX(gx) }}</text>
          </g>
          <g v-for="gy in yTicks" :key="'gy-'+gy">
            <line class="grid" x1="0" :x2="innerW" :y1="y(gy)" :y2="y(gy)"/>
            <text class="axis-label" :x="-6" :y="y(gy)" text-anchor="end" dominant-baseline="middle">{{ tickFmtY(gy) }}</text>
          </g>
					<line class="axis" x1="0" :y1="innerH" :x2="innerW" :y2="innerH"/>
  				<line class="axis" x1="0" y1="0" x2="0" :y2="innerH"/>  
					<polyline v-if="points.length" class="curve" :points="polyPoints" />
					<circle v-for="(pt,i) in points" :key="i" :cx="x(pt.i)" :cy="y(pt.u - baseU)" r="2.2" class="pt"/>
					
					<text class="axis-title" :x="innerW/2" :y="innerH+32" text-anchor="middle">Current I ({{ xUnit }})</text>
					<text class="axis-title" :x="-innerH/2" :y="-(margin.left - 12)" transform="rotate(-90)" text-anchor="middle">Voltage U ({{ yUnit }})</text>
				</g>
      </svg>
    </div>
  </div>
  <div v-else class="hint">No LPR data</div>
</template>

<script>
export default {
	name: "LprGraph",
	emits: ["img-export"],
  props: {
    voltageSeries: { type: Array, default: () => [] },
    currentSeries: { type: Array, default: () => [] },
    pairMap: { type: Array, default: () => [] },
    width:  { type: Number, default: 740 },
    height: { type: Number, default: 360 },
    margin: { type: Object, default: () => ({ top: 10, right: 16, bottom: 50, left: 72 }) },
		xMin:   { type: Number, default: null },
		xMax:   { type: Number, default: null },
		yMin:   { type: Number, default: null },
		yMax:   { type: Number, default: null },
		step:   { type: Number, default: null },
		scale:	{ type: Number, default: null},

		automaticReport: { type: Boolean, default: false }
  },
  data() {
    return { 
			pairIdx: 0,
			imgData: null,

		};
  },
	mounted() {
    if (this.automaticReport) {
      this.$nextTick(() => this.exportImg());
    }
  },
	watch: {
    points() {
      if (this.automaticReport) this.$nextTick(() => this.exportImg());
    }
  },
	methods: {
		getSvgString() {
			const svg = this.$refs.svgEl;
			if (!svg) return null;

			const clone = svg.cloneNode(true);
			clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
			clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");

			const props = [
				"fill","stroke","stroke-width","stroke-linecap","stroke-linejoin",
				"opacity","font-size","font-family","font-weight","font-style",
				"text-anchor","shape-rendering"
			];
			const all = [clone, ...clone.querySelectorAll("*")];
			all.forEach(el => {
				const cs = getComputedStyle(el);
				props.forEach(p => {
					const v = cs[p];
					if (v && v !== "normal") el.style[p] = v;
				});
			});

			return new XMLSerializer().serializeToString(clone);
		},

		exportImg() {
			if (this.automaticReport) {
				const src = this.getSvgString();
				if (!src) return null;
				const dataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(src);
				this.imgData = dataUrl;
				this.$emit("img-export", dataUrl);
				return dataUrl;
			}
		},

		fmtFixed(v, d) {
			return Number.isFinite(v) ? Number(v).toFixed(Math.max(0, d|0)) : "";
		},

		tickFmtX(v) { return this.fmtFixed(v, this.xDecimals); },

		tickFmtY(v) { return this.fmtFixed(v, this.yDecimals); },

		pairLabel(p) {
			const uName = p?.u?.label || p?.u?.code || `U${p?.u?.channelId ?? ''}`;
			const iName = p?.i?.label || p?.i?.code || `I${p?.i?.channelId ?? ''}`;
			const uFull = [p?.u?.nodeName, uName].filter(Boolean).join(' ');
			const iFull = [p?.i?.nodeName, iName].filter(Boolean).join(' ');

			const ARROW = '\u2009⟶\u2009';
			return `${uFull}${ARROW}${iFull}`;
		},

		x(v) {
			const a = this.xDomMin, b = this.xDomMax;
			return ((v - a) / (b - a || 1)) * this.innerW;
		},
		y(v) {
			const a = this.yDomMin, b = this.yDomMax;
			return (1 - (v - a) / (b - a || 1)) * this.innerH;
		},

		makeNice(min, max, count = 6) {
			if (!Number.isFinite(min) || !Number.isFinite(max)) { min = 0; max = 1; }
			if (min === max) { const pad = Math.abs(min || 1) * 0.05; min -= pad; max += pad; }
			const span = max - min;
			const step0 = span / Math.max(1, count);
			const pow = Math.pow(10, Math.floor(Math.log10(step0)));
			const cand = [1, 2, 5, 10].map(k => k * pow);
			const step = cand.find(s => step0 <= s) || cand[cand.length - 1];
			const niceMin = Math.floor(min / step) * step;
			const niceMax = Math.ceil(max / step) * step;

			const ticks = [];
			for (let v = niceMin; v <= niceMax + 1e-9; v += step) ticks.push(+v.toFixed(6));
			return { min: niceMin, max: niceMax, step, ticks };
		},

    tickFmt(v) { return Math.abs(v) >= 1000 ? v.toFixed(0) : v.toFixed(2); },
  },
  computed: {
		eCorr() {
			return this.points.length ? Math.round(this.points[0].u - (this.step * this.scale)) : 0;
		},

		baseU() { 
			return this.points.length ? this.points[0].u - (this.step * this.scale) : 0; 
		},
		xDecimals() {
			const d = this.currentSeries?.[0]?.decimals;
			return Number.isFinite(d) ? d : 3;
		},

		yDecimals() {
			const d = this.voltageSeries?.[0]?.decimals;
			return Number.isFinite(d) ? d : 3;
		},

    innerW() { return this.width  - this.margin.left - this.margin.right; },

    innerH() { return this.height - this.margin.top  - this.margin.bottom; },

    pairs() {
      const vById = new Map(this.voltageSeries.map(s => [s.channelId, s]));
      const iById = new Map(this.currentSeries.map(s => [s.channelId, s]));

      if (this.pairMap.length) {
        return this.pairMap.map(p => {
          const u = vById.get(p.uId), i = iById.get(p.iId);
          return (u && i) ? { u, i, label: p.label } : null;
        }).filter(Boolean);
      }

      const n = Math.min(this.voltageSeries.length, this.currentSeries.length);
      const out = [];
      for (let k = 0; k < n; k++) {
        out.push({
          u: this.voltageSeries[k],
          i: this.currentSeries[k],
          label: (this.voltageSeries[k]?.label || this.currentSeries[k]?.label) || null
        });
      }
      return out;
    },

    points() {
      const pair = this.pairs[this.pairIdx];
      if (!pair) return [];
      const getVal = r => Array.isArray(r) ? r[2] : null;
      const getTs  = r => Array.isArray(r) ? r[0] : null;

      const uByTs = new Map();
      for (const row of (pair.u.data || [])) {
        const ts = getTs(row), val = getVal(row);
        if (ts != null && Number.isFinite(val)) uByTs.set(String(ts), { ts, u: val });
      }

      const joined = [];
      for (const row of (pair.i.data || [])) {
        const ts = String(getTs(row));
        const iVal = getVal(row);
        if (!Number.isFinite(iVal)) continue;
        const uRec = uByTs.get(ts);
        if (uRec) joined.push({ ts: uRec.ts, u: uRec.u, i: iVal });
      }

      joined.sort((a,b) => a.ts - b.ts);
      return joined;
    },

    xDataMin() { return Number.isFinite(this.xMin) ? this.xMin : (this.points.length ? Math.min(...this.points.map(p => p.i)) : 0); },

		xDataMax() { return Number.isFinite(this.xMax) ? this.xMax : (this.points.length ? Math.max(...this.points.map(p => p.i)) : 1); },

		yDataMin() {
			if (Number.isFinite(this.yMin)) return this.yMin;
			if (!this.points.length) return 0;
			const ys = this.points.map(p => p.u - this.baseU);
			return Math.min(...ys);
		},

		yDataMax() {
			if (Number.isFinite(this.yMax)) return this.yMax;
			if (!this.points.length) return 1;
			const ys = this.points.map(p => p.u - this.baseU);
			return Math.max(...ys);
		},

		xNice() { return this.makeNice(this.xDataMin, this.xDataMax, 6); },

		yNice() { return this.makeNice(this.yDataMin, this.yDataMax, 6); },

		xDomMin() { return this.xNice.min; },

		xDomMax() { return this.xNice.max; },

		yDomMin() { return this.yNice.min; },

		yDomMax() { return this.yNice.max; },

		xTicks() { return this.xNice.ticks; },

		yTicks() { return this.yNice.ticks; },

		polyPoints() {
			return this.points.map(p => `${this.x(p.i)},${this.y(p.u - this.baseU)}`).join(' ');
		},

		xUnit() { return this.currentSeries?.[0]?.unit || ""; },

  	yUnit() { return this.voltageSeries?.[0]?.unit || ""; },
  },
};
</script>

<style scoped>
.lpr-wrap { display: flex; flex-direction: column; gap: 8px; }
.toolbar { display: flex; align-items: center; gap: 12px; font-size: 13px; }
.svg-scroller { overflow: auto; border: 1px solid #eee; border-radius: 6px; }
.grid { stroke:#e5e7eb; stroke-width:1; }
.axis { stroke:#374151; stroke-width:1.25; shape-rendering:crispEdges; }
.axis-label { fill:#6b7280; font-size:11px; }
.axis-title { fill:#374151; font-size:12px; font-weight:700; }
.pt { fill:#2563eb; opacity:.9; }
.curve { fill:none; stroke:#2563eb; stroke-width:1.5; opacity:.8; }
.hint { color:#666; }
</style>
